// Shared color tokens for deterministic naming components.
// Extracted from hardcoded values for consistency and future theming.

// Blue palette underpinning the suggestion-card chrome. Named by role, not
// hue, so callsites don't depend on the specific shade. Hexes kept explicit
// (rather than alpha-derived) for pixel-exact parity with the accepted design.
const BLUE_CARD_BG = '#e7f3ff';
const BLUE_CARD_BORDER = '#bfdbf7';
const BLUE_INPUT_BORDER = '#d6e4f5'; // soft idle border on input/view boxes
const BLUE_INPUT_BORDER_STRONG = '#90caf9'; // post-accept edit-mode border
const BLUE_INPUT_BG = '#fafcff';
const BLUE_HOVER_ACCENT = '#42a5f5';

export const COLORS = {
  suggestion: {
    cardBg: BLUE_CARD_BG,
    cardBorder: BLUE_CARD_BORDER,
    inputBorder: BLUE_INPUT_BORDER,
    inputBorderStrong: BLUE_INPUT_BORDER_STRONG,
    inputBg: BLUE_INPUT_BG,
    hoverAccent: BLUE_HOVER_ACCENT,
  },
  chip: {
    selected: { bg: '#eceef1', border: '#c4c9d0', text: '#2c3e50' },
    available: { bg: '#fafbfc', border: '#e0e4ea', borderHover: '#b8c0cc', text: '#4a5568' },
    view: { bg: '#fff', border: '#dde2e8', text: '#454f5b' },
  },
  text: {
    primary: '#212b36',
    secondary: '#637381',
    /** Instructional / helper copy — secondary but comfortably readable */
    helper: '#5a6573',
    disabled: '#919eab',
    placeholder: '#c8d0d8',
    subtle: '#b0b8c1',
    input: '#344563',
  },
  icon: {
    secondary: '#637381',
    disabled: '#919eab',
    action: '#1976d2',
  },
  accept: {
    border: '#c8e6c9',
    color: '#4caf50',
    bgHover: '#e8f5e9',
    filled: '#4caf50',
    filledHover: '#43a047',
    focusRing: 'rgba(76, 175, 80, 0.35)',
  },
  surface: {
    bg: '#f8f9fa',
    border: '#eef0f2',
    divider: '#e0e4ea',
    hover: '#f0f2f5',
    hoverBorder: '#d0d5dd',
  },
  modal: {
    headerGradient: 'linear-gradient(135deg, #f0f4f8 0%, #e8eef4 100%)',
    headerBorder: '#e0e6ed',
    preview: { bg: '#f0f7ff', border: '#d0e3f7' },
    link: '#5b8fd9',
    linkHover: '#3366cc',
    save: '#3366cc',
    saveHover: '#2952a3',
    dragActive: '#5b8fd9',
  },
  // Tokens for the redesigned "File naming pattern" modal. Pale blue-gray
  // header band + subtle indigo emphasis, with a prominent before/after
  // preview. Kept separate from `modal` so the older suggestion-card chrome
  // is unaffected.
  templateModal: {
    headerBg: '#f4f6fa',
    headerBorder: '#e4e9f0',
    iconBg: '#e7eefc',
    iconColor: '#3366cc',
    badgeBg: '#eef2fb',
    badgeBorder: '#dbe2f2',
    badgeText: '#4759a8',
    sectionTitle: '#212b36',
    sectionHelper: '#5a6573',
    footerHelper: '#5a6573',
    availableLabel: '#5a6573',
    editorBorder: '#dfe3ea',
    editorBg: '#fafbfc',
    editorBorderActive: '#90caf9',
    editorBgActive: '#fafcff',
    panelBg: '#eef4fb',
    panelBorder: '#cddaea',
    panelShadow: '0 2px 10px rgba(51, 102, 204, 0.1)',
    beforeBg: '#ffffff',
    beforeBorder: '#d8dee8',
    beforeLabel: '#5a6573',
    afterBg: '#e3f0ff',
    afterBorder: '#7eb3ea',
    afterLabel: '#3366cc',
    arrowBorder: '#c5d6ea',
    arrowBg: '#ffffff',
    arrowColor: '#3366cc',
    fieldBg: '#ffffff',
    fieldBorder: '#d8dee8',
    fieldLabel: '#637381',
    previewTitle: '#212b36',
  },
  remove: {
    bg: '#919eab',
    bgHover: '#637381',
  },
  separator: {
    edit: { border: '#c4c9d0', text: '#2c3e50', hoverBorder: '#90caf9', hoverBg: '#f0f7ff' },
    view: { border: '#dde2e8', text: '#454f5b' },
  },
  drag: {
    border: '#90caf9',
    bg: '#e3f2fd',
    shadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
} as const;

export const TOAST_DURATION = 3000;
